// =====================================================================================================
// Packages, billing and subscriptions — the web mirror of the API's vocabulary
// (CryptoAPI/Services/Billing/BillingCatalog.cs). One place for billing types, cycles, statuses, apps,
// limits, currencies and money/period labels, so the package editor, the developer billing pages, the
// store's billing page and the upgrade page all read the same way.
// =====================================================================================================
import { PAYMENT_CURRENCY_OPTIONS, PaymentCurrencyOption } from 'src/Common/constants/paymentCurrencies';

// ── Billing types ────────────────────────────────────────────────────────────────────────────────────
export type BillingType = 'Subscription' | 'PerBill';
export const BILLING_TYPE_SUBSCRIPTION: BillingType = 'Subscription';
export const BILLING_TYPE_PER_BILL: BillingType = 'PerBill';

export const BILLING_TYPE_OPTIONS: { value: BillingType; label: string; description: string }[] = [
  { value: 'Subscription', label: 'Fixed period', description: 'A set price for each period (monthly, yearly, …).' },
  {
    value: 'PerBill',
    label: 'Pay as you go (per bill)',
    description: 'No fixed fee. The store is charged for each sale bill it creates, billed monthly.'
  }
];

export function isPerBill(value?: string | null): boolean {
  const v = String(value ?? '').replace(/[\s_-]/g, '').toLowerCase();
  return v === 'perbill' || v === 'payasyougo';
}

export function normalizeBillingType(value?: string | null): BillingType {
  return isPerBill(value) ? 'PerBill' : 'Subscription';
}

// ── Billing cycles (fixed period) ────────────────────────────────────────────────────────────────────
export type BillingCycle = 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly' | 'Custom';

export const BILLING_CYCLE_OPTIONS: { value: BillingCycle; label: string; months: number | null; per: string }[] = [
  { value: 'Monthly', label: 'Monthly', months: 1, per: 'month' },
  { value: 'Quarterly', label: 'Quarterly (3 months)', months: 3, per: 'quarter' },
  { value: 'Half-Yearly', label: 'Half-yearly (6 months)', months: 6, per: '6 months' },
  { value: 'Yearly', label: 'Yearly', months: 12, per: 'year' },
  { value: 'Custom', label: 'Custom period (days)', months: null, per: 'period' }
];

/** The canonical cycle for a stored value ("Half Yearly", "annual", …); null when blank. */
export function normalizeCycle(value?: string | null): BillingCycle | null {
  const v = String(value ?? '').trim().toLowerCase();
  if (!v) return null;
  if (v.includes('half') || v.includes('semi') || v.includes('six')) return 'Half-Yearly';
  if (v.includes('quarter')) return 'Quarterly';
  if (v.includes('year') || v.includes('annual')) return 'Yearly';
  if (v.includes('custom')) return 'Custom';
  if (v.includes('month')) return 'Monthly';
  return null;
}

export function monthsInCycle(cycle?: string | null): number | null {
  return BILLING_CYCLE_OPTIONS.find((o) => o.value === normalizeCycle(cycle))?.months ?? null;
}

/** "month", "quarter", "6 months", "year", "30 days". */
export function cyclePer(cycle?: string | null, customDays?: number | null): string {
  const c = normalizeCycle(cycle);
  if (c === 'Custom') return customDays && customDays > 0 ? `${customDays} days` : 'period';
  return BILLING_CYCLE_OPTIONS.find((o) => o.value === c)?.per ?? 'month';
}

// ── Subscription status ──────────────────────────────────────────────────────────────────────────────
export type SubscriptionStatus = 'Active' | 'Suspended' | 'Cancelled' | 'Expired' | 'Trial';
/** What the developer can set (Expired / Trial are derived). */
export const SETTABLE_STATUS_OPTIONS: { value: 'Active' | 'Suspended' | 'Cancelled'; label: string; hint: string }[] = [
  { value: 'Active', label: 'Active', hint: 'The store can use the system until the end date.' },
  { value: 'Suspended', label: 'Suspended', hint: 'Access ends now. Set Active again to restore it.' },
  { value: 'Cancelled', label: 'Cancelled', hint: 'Access ends now and the subscription is closed.' }
];

export function statusTone(status?: string | null): 'success' | 'warning' | 'error' | 'neutral' | 'info' {
  switch (String(status ?? '').toLowerCase()) {
    case 'active':
      return 'success';
    case 'trial':
      return 'info';
    case 'suspended':
      return 'warning';
    case 'cancelled':
    case 'canceled':
    case 'expired':
      return 'error';
    default:
      return 'neutral';
  }
}

// ── Open-ended (pay-as-you-go) subscriptions ─────────────────────────────────────────────────────────
/** A PerBill subscription runs to 31 Dec 2099 (BillingDates.OpenEndedUntilUtc on the server). */
export function isOpenEndedDate(value?: string | Date | null): boolean {
  if (!value) return false;
  const d = value instanceof Date ? value : new Date(String(value));
  return !Number.isNaN(d.getTime()) && d.getUTCFullYear() >= 2099;
}

/** "Pay as you go" for open-ended / PerBill subscriptions, otherwise "N days left" (or "Expired"). */
export function validityLabel(opts: {
  billingType?: string | null;
  endDate?: string | null;
  daysRemaining?: number | null;
  isExpired?: boolean;
}): string {
  if (isPerBill(opts.billingType) || isOpenEndedDate(opts.endDate)) return 'Pay as you go';
  if (opts.isExpired) return 'Expired';
  const d = Number(opts.daysRemaining);
  if (!Number.isFinite(d)) return '—';
  return d === 1 ? '1 day left' : `${Math.max(0, Math.round(d))} days left`;
}

// ── Apps a package can include ───────────────────────────────────────────────────────────────────────
export type PackageAppKey = 'desktop' | 'mobile' | 'waiter' | 'kds';
export const PACKAGE_APPS: { key: PackageAppKey; label: string; description: string }[] = [
  { key: 'desktop', label: 'Desktop POS app', description: 'The Windows cashier app (works offline).' },
  { key: 'mobile', label: 'Mobile POS app', description: 'The Android / iOS POS app.' },
  { key: 'waiter', label: 'TableTime waiter app', description: 'Order taking at the table.' },
  { key: 'kds', label: 'Kitchen display (KDS) app', description: 'Kitchen screens and ticket printing.' }
];

/** Included app keys from an API value: null/undefined = every app (older packages). */
export function includedAppKeys(value?: string[] | string | null): PackageAppKey[] {
  if (value === null || value === undefined) return PACKAGE_APPS.map((a) => a.key);
  const list = Array.isArray(value) ? value : String(value).split(',');
  const wanted = new Set(list.map((k) => String(k).trim().toLowerCase()));
  return PACKAGE_APPS.filter((a) => wanted.has(a.key)).map((a) => a.key);
}

// ── Limits ───────────────────────────────────────────────────────────────────────────────────────────
export type PackageLimitKey =
  | 'users'
  | 'branches'
  | 'items'
  | 'customers'
  | 'devices'
  | 'billsPerMonth'
  | 'storageMb'
  | 'aiRequestsPerMonth'
  | 'aiImagesPerMonth'
  | 'messagesPerMonth'
  | 'dataRetentionMonths';

export interface PackageLimitDef {
  key: PackageLimitKey;
  label: string;
  description: string;
  unit: 'count' | 'MB' | 'months';
  perMonth: boolean;
  /** Field name on the plan API (SavePlanRequest / catalog). */
  planField: string;
}

/** Same order and meaning as the API's PackageLimits.All. Empty = unlimited. */
export const PACKAGE_LIMITS: PackageLimitDef[] = [
  { key: 'users', label: 'Users', description: 'Staff accounts that can sign in.', unit: 'count', perMonth: false, planField: 'maxUsers' },
  { key: 'branches', label: 'Branches', description: 'Active branches (outlets / warehouses).', unit: 'count', perMonth: false, planField: 'maxBranches' },
  { key: 'items', label: 'Items', description: 'Products and services in the catalog.', unit: 'count', perMonth: false, planField: 'maxItems' },
  { key: 'customers', label: 'Customers', description: 'Saved customer accounts.', unit: 'count', perMonth: false, planField: 'maxCustomers' },
  {
    key: 'devices',
    label: 'Devices',
    description: 'Registered app installs (desktop, mobile, TableTime, kitchen). Each keeps a live sync connection.',
    unit: 'count',
    perMonth: false,
    planField: 'maxDevices'
  },
  { key: 'billsPerMonth', label: 'Bills per month', description: 'Sale bills created per calendar month.', unit: 'count', perMonth: true, planField: 'maxTransactionsPerMonth' },
  { key: 'storageMb', label: 'Storage', description: 'Data and file storage.', unit: 'MB', perMonth: false, planField: 'storageLimitMb' },
  {
    key: 'aiRequestsPerMonth',
    label: 'AI assistant requests / month',
    description: 'Questions to the AI assistant and AI suggestions (paid per use).',
    unit: 'count',
    perMonth: true,
    planField: 'maxAiRequestsPerMonth'
  },
  { key: 'aiImagesPerMonth', label: 'AI images / month', description: 'Item images generated with AI (paid per image).', unit: 'count', perMonth: true, planField: 'maxAiImagesPerMonth' },
  {
    key: 'messagesPerMonth',
    label: 'Emails & messages / month',
    description: 'Emails and WhatsApp/SMS the system sends (receipts, reports, marketing).',
    unit: 'count',
    perMonth: true,
    planField: 'maxMessagesPerMonth'
  },
  {
    key: 'dataRetentionMonths',
    label: 'Data history kept',
    description: 'Months of transaction history kept online; older data can be archived.',
    unit: 'months',
    perMonth: false,
    planField: 'dataRetentionMonths'
  }
];

/** A company override meaning "unlimited" even when the package has a limit (API: -1). */
export const UNLIMITED_OVERRIDE = -1;

/** "500 MB", "2 GB", "1.5 GB" (1 GB = 1024 MB). */
export function formatStorageMb(mb?: number | null): string {
  if (mb === null || mb === undefined || !Number.isFinite(Number(mb))) return 'Unlimited';
  const v = Number(mb);
  if (v >= 1024) {
    const gb = v / 1024;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(gb < 10 ? 2 : 1).replace(/\.?0+$/, '')} GB`;
  }
  return `${Math.round(v)} MB`;
}

/** MB from a value typed with a unit. Empty → null (unlimited). */
export function storageInputToMb(value: string | number | null | undefined, unit: 'MB' | 'GB'): number | null {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(unit === 'GB' ? n * 1024 : n);
}

/** The best unit to show an MB value in (GB when it is a whole number of GB or ≥ 10 GB). */
export function storageDisplayUnit(mb?: number | null): { value: string; unit: 'MB' | 'GB' } {
  if (mb === null || mb === undefined) return { value: '', unit: 'GB' };
  const v = Number(mb);
  if (v >= 1024 && (v % 1024 === 0 || v >= 10240)) return { value: String(+(v / 1024).toFixed(2)), unit: 'GB' };
  return { value: String(Math.round(v)), unit: 'MB' };
}

/** "Unlimited", "5", "500 MB", "24 months", "1,000 / month". */
export function formatLimitValue(def: Pick<PackageLimitDef, 'unit' | 'perMonth'>, value?: number | null): string {
  if (value === null || value === undefined || Number(value) < 0) return 'Unlimited';
  if (def.unit === 'MB') return formatStorageMb(value);
  if (def.unit === 'months') return `${value} ${Number(value) === 1 ? 'month' : 'months'}`;
  const n = Number(value).toLocaleString('en-US');
  return def.perMonth ? `${n} / month` : n;
}

// ── Currencies & money ───────────────────────────────────────────────────────────────────────────────
export interface CurrencyRef {
  code?: string | null;
  symbol?: string | null;
}

/** ISO code of a display symbol (null when unknown or ambiguous — ¥ is both CNY and JPY). */
export function currencyCodeFromSymbol(symbol?: string | null): string | null {
  const s = String(symbol ?? '').trim();
  if (!s) return null;
  if (/^[A-Za-z]{3}$/.test(s) && PAYMENT_CURRENCY_OPTIONS.some((c) => c.code === s.toUpperCase())) return s.toUpperCase();
  if (/^(rs\.?|₨)$/i.test(s)) return 'PKR';
  const matches = PAYMENT_CURRENCY_OPTIONS.filter((c) => c.symbol === s);
  return matches.length === 1 ? matches[0].code : null;
}

/** The currency option for a (code, symbol) pair: code first, then an unambiguous symbol. */
export function currencyFor(ref?: CurrencyRef | null): PaymentCurrencyOption | null {
  const code = String(ref?.code ?? '').trim().toUpperCase();
  if (code) {
    const byCode = PAYMENT_CURRENCY_OPTIONS.find((c) => c.code === code);
    if (byCode) return byCode;
  }
  const fromSymbol = currencyCodeFromSymbol(ref?.symbol);
  return fromSymbol ? PAYMENT_CURRENCY_OPTIONS.find((c) => c.code === fromSymbol) ?? null : null;
}

/** The display symbol: the stored symbol, else the code's symbol, else the code, else "$". */
export function currencySymbolOf(ref?: CurrencyRef | null): string {
  const s = String(ref?.symbol ?? '').trim();
  if (s) return s;
  return currencyFor(ref)?.symbol ?? (String(ref?.code ?? '').trim().toUpperCase() || '$');
}

const RTL_SYMBOL = /[֐-ࣿיִ-﷿ﹰ-﻿]/;

/** "Rs 2,500.00", "$29.00", "Rs 2.00" (rates keep up to 4 decimals: "$0.025"). */
export function formatMoney(amount: number | string | null | undefined, currency?: CurrencyRef | null, opts?: { rate?: boolean }): string {
  const n = Number(amount);
  const sym = currencySymbolOf(currency);
  if (!Number.isFinite(n)) return `${sym} 0.00`;
  const decimals = opts?.rate ? Math.min(4, Math.max(2, (String(n).split('.')[1] ?? '').length)) : 2;
  const body = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  // Symbols of letters ("Rs", "CHF", "KD") read better with a space; "$", "€", "£" without.
  const spaced = /[A-Za-z]$/.test(sym) || sym.length > 1;
  // A right-to-left symbol ("د.إ", "﷼") would pull the figures after it out of order in a left-to-right line
  // ("45 / 9,000.00 د.إ days"); a left-to-right mark after it keeps "د.إ 9,000.00 / 45 days".
  const mark = RTL_SYMBOL.test(sym) ? '‎' : '';
  return `${n < 0 ? '-' : ''}${sym}${mark}${spaced ? ' ' : ''}${body}`;
}

/** A package's price line: "Rs 2,500.00 / month", "$29.00 / 30 days", "Rs 2.00 per bill", "Custom". */
export function packagePriceLabel(pkg: {
  billingType?: string | null;
  price?: number | null;
  pricePerBill?: number | null;
  currencyCode?: string | null;
  currencySymbol?: string | null;
  billingCycle?: string | null;
  billingPeriod?: string | null;
  customPeriodDays?: number | null;
  isCustom?: boolean;
}): string {
  const cur = { code: pkg.currencyCode, symbol: pkg.currencySymbol };
  if (isPerBill(pkg.billingType)) return `${formatMoney(pkg.pricePerBill ?? 0, cur, { rate: true })} per bill`;
  if (pkg.isCustom && !(Number(pkg.price) > 0)) return 'Custom pricing';
  return `${formatMoney(pkg.price ?? 0, cur)} / ${cyclePer(pkg.billingCycle ?? pkg.billingPeriod, pkg.customPeriodDays)}`;
}

// ── Dates ────────────────────────────────────────────────────────────────────────────────────────────
/** "20 Oct 2026" for an API date (UTC instants and date-only strings alike); "—" when empty. */
export function formatBillingDate(value?: string | Date | null): string {
  if (!value) return '—';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  const s = typeof value === 'string' && /T\d{2}:\d{2}/.test(value) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? `${value}Z` : value;
  const d = s instanceof Date ? s : new Date(String(s));
  if (Number.isNaN(d.getTime())) return '—';
  if (d.getUTCFullYear() >= 2099) return 'No end date';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
